"use client";

import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Button } from "./ui/button";
import {
  X,
  User,
  Loader2,
  DollarSign,
  Clock,
  IdCard,
  Phone,
  Mail,
  Calendar,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import playerProfileService from "../apis/playerProfileService";

const formatMoney = (value) => {
  const numeric = parseFloat(value);
  if (!Number.isFinite(numeric)) return "0.00";
  return numeric.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPlaytime = (totalMins) => {
  const mins = Math.max(0, Math.round(Number(totalMins) || 0));
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  if (hours <= 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
};

const normalizeName = (name) =>
  (name || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

export default function PromosPlayerDetailsModal({
  isOpen,
  playerName,
  promoTransactions = [],
  onClose,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    if (!isOpen || !playerName) return;

    let cancelled = false;

    const loadPlayerData = async () => {
      setLoading(true);
      setError(null);
      setProfile(null);
      setEntries([]);

      try {
        const searchResponse = await playerProfileService.getDistinctPlayers(
          1,
          50,
          playerName
        );

        const players = searchResponse?.data || [];
        const target = normalizeName(playerName);
        const matched =
          players.find((p) => normalizeName(p.player_name) === target) ||
          players.find((p) =>
            normalizeName(p.player_name).includes(target)
          ) ||
          players[0] ||
          null;

        if (!matched?.player_number) {
          if (!cancelled) {
            setError(
              "Player profile not found in Poker Atlas. Showing promo history only."
            );
          }
          return;
        }

        if (!cancelled) setProfile(matched);

        const entriesResponse = await playerProfileService.getPlayerEntries(
          matched.player_number
        );

        if (!cancelled && entriesResponse?.success) {
          setEntries(Array.isArray(entriesResponse.data) ? entriesResponse.data : []);
        }
      } catch (err) {
        console.error("Failed to load player details:", err);
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load player details from Poker Atlas"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPlayerData();

    return () => {
      cancelled = true;
    };
  }, [isOpen, playerName]);

  const atlasStats = useMemo(() => {
    let totalWon = 0;
    let totalLost = 0;
    let totalSessionMins = 0;

    const logs = entries.map((entry) => {
      const points = parseFloat(entry.cash_points) || 0;
      const sessionMins = parseInt(entry.session_mins, 10) || 0;
      totalSessionMins += sessionMins;

      if (points >= 0) {
        totalWon += points;
      } else {
        totalLost += Math.abs(points);
      }

      return {
        id: entry.id,
        date: entry.open_date || entry.closed_date || entry.created_at,
        game: entry.game_name || "-",
        location: entry.location || "-",
        sessionMins,
        cashPoints: points,
        result: points >= 0 ? "won" : "lost",
      };
    });

    const avgPlaytime =
      logs.length > 0 ? totalSessionMins / logs.length : 0;

    return {
      totalWon,
      totalLost,
      avgPlaytime,
      logs: logs.sort((a, b) => new Date(b.date) - new Date(a.date)),
    };
  }, [entries]);

  const promoStats = useMemo(() => {
    const rows = (promoTransactions || []).filter(
      (tx) => normalizeName(tx.player_name) === normalizeName(playerName)
    );

    const spent = rows
      .filter((tx) => tx.transaction_type === "withdraw")
      .reduce((sum, tx) => sum + (parseFloat(tx.transaction_amount) || 0), 0);

    const returned = rows
      .filter((tx) => tx.transaction_type === "deposit")
      .reduce((sum, tx) => sum + (parseFloat(tx.transaction_amount) || 0), 0);

    return {
      spent,
      returned,
      netSpend: spent - returned,
      rows: rows
        .slice()
        .sort((a, b) => new Date(b.date) - new Date(a.date)),
    };
  }, [promoTransactions, playerName]);

  if (!isOpen || !playerName) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {profile?.player_name || playerName}
              </h2>
              <p className="text-sm text-gray-500">
                Player details from Poker Atlas + promo history
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              {error && (
                <div className="p-3 rounded-md bg-amber-50 text-amber-800 text-sm border border-amber-200">
                  {error}
                </div>
              )}

              {/* Profile fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-slate-50 border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                    <IdCard className="h-4 w-4" />
                    Unique Player ID
                  </div>
                  <p className="text-lg font-semibold text-slate-900">
                    {profile?.player_number || "N/A"}
                  </p>
                </div>
                <div className="bg-slate-50 border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                    <Phone className="h-4 w-4" />
                    Phone
                  </div>
                  <p className="text-lg font-semibold text-slate-900">
                    {profile?.phone || "N/A"}
                  </p>
                </div>
                <div className="bg-slate-50 border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                    <Mail className="h-4 w-4" />
                    Email
                  </div>
                  <p className="text-lg font-semibold text-slate-900 truncate">
                    {profile?.email || "N/A"}
                  </p>
                </div>
                <div className="bg-slate-50 border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                    <Calendar className="h-4 w-4" />
                    Member Since
                  </div>
                  <p className="text-lg font-semibold text-slate-900">
                    {profile?.member_since
                      ? moment(profile.member_since).format("MM/DD/YYYY")
                      : "N/A"}
                  </p>
                </div>
              </div>

              {/* Key numbers */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 text-sm mb-1">
                    <TrendingUp className="h-4 w-4" />
                    Total $ Won
                  </div>
                  <p className="text-2xl font-bold text-green-800">
                    ${formatMoney(atlasStats.totalWon)}
                  </p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 text-sm mb-1">
                    <TrendingDown className="h-4 w-4" />
                    Total $ Lost
                  </div>
                  <p className="text-2xl font-bold text-red-800">
                    ${formatMoney(atlasStats.totalLost)}
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-700 text-sm mb-1">
                    <Clock className="h-4 w-4" />
                    Avg Playtime
                  </div>
                  <p className="text-2xl font-bold text-blue-800">
                    {formatPlaytime(atlasStats.avgPlaytime)}
                  </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-700 text-sm mb-1">
                    <DollarSign className="h-4 w-4" />
                    Promo Spend (Net)
                  </div>
                  <p className="text-2xl font-bold text-amber-800">
                    ${formatMoney(promoStats.netSpend)}
                  </p>
                </div>
              </div>

              {/* Win / Loss logs from Poker Atlas sessions */}
              <div className="bg-white rounded-lg border">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Winning & Losing Logs
                  </h3>
                  <p className="text-sm text-gray-500">
                    Session results from Poker Atlas ({atlasStats.logs.length} sessions)
                  </p>
                </div>
                <div className="overflow-x-auto max-h-72">
                  {atlasStats.logs.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      No session logs found for this player.
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Game
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Playtime
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Result
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {atlasStats.logs.slice(0, 50).map((log, index) => (
                          <tr key={log.id || index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {log.date
                                ? moment(log.date).format("MM/DD/YYYY hh:mm A")
                                : "-"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {log.game}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatPlaytime(log.sessionMins)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                  log.result === "won"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {log.result === "won" ? "Won" : "Lost"}
                              </span>
                            </td>
                            <td
                              className={`px-4 py-3 text-sm font-semibold ${
                                log.result === "won"
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {log.result === "won" ? "+" : "-"}$
                              {formatMoney(Math.abs(log.cashPoints))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Promo transaction logs */}
              <div className="bg-white rounded-lg border">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Promo Transactions
                  </h3>
                  <p className="text-sm text-gray-500">
                    Spent ${formatMoney(promoStats.spent)} · Returned $
                    {formatMoney(promoStats.returned)}
                  </p>
                </div>
                <div className="overflow-x-auto max-h-60">
                  {promoStats.rows.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      No promo transactions for this player in the current filter.
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Promotion
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Added By
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {promoStats.rows.slice(0, 30).map((tx, index) => (
                          <tr key={tx.id || index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {moment(tx.date).format("MM/DD/YYYY hh:mm A")}
                            </td>
                            <td className="px-4 py-3 text-sm capitalize">
                              {tx.transaction_type}
                            </td>
                            <td
                              className={`px-4 py-3 text-sm font-semibold ${
                                tx.transaction_type === "withdraw"
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              {tx.transaction_type === "withdraw" ? "-" : "+"}$
                              {formatMoney(tx.transaction_amount)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {tx.notes || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {tx.added_by || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 sticky bottom-0">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
